import type { ChannelAgent } from "@/services/conversations.api";

import type { AgentFuse, AgentSummary, WorkerKeyStatus } from "./api";

type ChannelAgentRef = Pick<ChannelAgent, "userId" | "displayName">;

/**
 * Lo que comparten la lista de asistentes del store, su drawer, la fila de Operadores del Store y
 * la ficha del canal (D62–D64). Funciones puras: sin antd ni API, para poder testearlas.
 */

export const PROVEEDORES: Record<string, string> = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  google: "Google",
};

/** Con el nombre de su config se presenta y se lo arroba; sin config, el del usuario. */
export function nombreDelAsistente(
  a: Pick<AgentSummary, "userId" | "displayName" | "name">,
): string {
  return a.displayName?.trim() || a.name?.trim() || `Asistente ${a.userId}`;
}

/** «Anthropic · claude-sonnet-5-5», con el del automático si es otro. */
export function modeloDelAsistente(
  a: Pick<AgentSummary, "provider" | "model" | "modelAuto">,
): string {
  if (!a.provider) return "Sin configurar";
  const proveedor = PROVEEDORES[a.provider] ?? a.provider;
  if (!a.model) return proveedor;
  const auto =
    a.modelAuto && a.modelAuto !== a.model
      ? ` · automático: ${a.modelAuto}`
      : "";
  return `${proveedor} · ${a.model}${auto}`;
}

/** Por usuario: para marcar en Operadores del Store las filas que son asistentes. */
export function asistentesPorUsuario(
  lista: AgentSummary[],
): Map<number, AgentSummary> {
  return new Map(lista.map((a) => [Number(a.userId), a]));
}

export interface OpcionDeAsistente {
  value: number;
  label: string;
  disabled?: boolean;
}

const porNombre = (x: OpcionDeAsistente, y: OpcionDeAsistente) =>
  x.label.localeCompare(y.label, "es");

/**
 * «Copiar conocimiento de…»: los otros asistentes del store, por nombre. Uno que todavía no tiene
 * conocimiento se ve, pero no se puede elegir: copiarlo dejaría una versión vacía.
 */
export function opcionesParaCopiar(
  lista: AgentSummary[],
  userId: number,
): OpcionDeAsistente[] {
  return lista
    .filter((a) => a.userId !== userId)
    .map((a) => ({
      value: a.userId,
      label: a.knowledgeVersion
        ? nombreDelAsistente(a)
        : `${nombreDelAsistente(a)} · sin conocimiento`,
      disabled: !a.knowledgeVersion,
    }))
    .sort(porNombre);
}

/**
 * Quién atiende el canal en automático (D64), entre los asistentes del store. Los apagados se
 * ofrecen marcados; el que el canal tiene y ya no es asistente se muestra igual, para que el
 * select no quede con un número.
 */
export function opcionesDelCanal(
  lista: AgentSummary[],
  actual: ChannelAgentRef | null,
): OpcionDeAsistente[] {
  const opciones = lista
    .map((a) => ({
      value: a.userId,
      label: a.enabled
        ? nombreDelAsistente(a)
        : `${nombreDelAsistente(a)} · apagado`,
    }))
    .sort(porNombre);
  if (actual && !lista.some((a) => a.userId === actual.userId))
    opciones.push({
      value: actual.userId,
      label: `${
        actual.displayName || `Usuario ${actual.userId}`
      } · ya no es asistente`,
    });
  return opciones;
}

/** En un canal nuevo se propone el asistente si no hay a quién más elegir: el único encendido, o el único. */
export function asistentePorDefecto(lista: AgentSummary[]): number | null {
  const encendidos = lista.filter((a) => a.enabled);
  if (encendidos.length === 1) return encendidos[0].userId;
  if (lista.length === 1) return lista[0].userId;
  return null;
}

/** El fusible saltado de ESTE asistente: `fuse` viene por usuario. */
export function fusibleDe(
  fuse: AgentFuse[] | undefined,
  userId: number,
): AgentFuse | undefined {
  return (fuse ?? []).find((f) => f.disabled && Number(f.userId) === userId);
}

/** Lo que se pega en las variables del workload de SleakOps (D63): una vez por store. */
export function envParaSleakOps(storeCode: string, key: string): string {
  return `STORE_CODE=${storeCode}\nAGENT_WORKER_KEY=${key}`;
}

/**
 * La confirmación antes de generar. Las dos cortan al pod: rotar invalida la clave y las sesiones;
 * la primera hace que los asistentes ya no entren con contraseña, que es como entra el pod viejo.
 */
export function avisoDeCredencial(k: WorkerKeyStatus | null): {
  titulo: string;
  descripcion: string;
  boton: string;
} {
  if (k?.exists)
    return {
      titulo: "¿Rotar la credencial del worker?",
      descripcion:
        "La clave actual deja de valer y se cortan las sesiones de los asistentes: el pod se detiene hasta que cargues la nueva en SleakOps y lo vuelvas a deployar.",
      boton: "Rotar",
    };
  return {
    titulo: "¿Generar la credencial del worker?",
    descripcion:
      "Desde ese momento los asistentes de este Store ya no entran con contraseña: el pod actual se corta hasta que cargues la clave en SleakOps y lo vuelvas a deployar.",
    boton: "Generar",
  };
}
